import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCurrentUser } from '../store/authSlice';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'merchant' | 'customer';
  // Merchant staff are below every role-gated route by default, but a staffer
  // holding this permission may access a merchant-gated route (the backend
  // enforces the same permission + scopes them to the owner merchant).
  staffPermission?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, requiredRole, staffPermission }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated, user, token, isLoading, staff } = useAppSelector((state) => state.auth);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('[AuthGuard] Checking auth:', { hasToken: !!token, hasUser: !!user, isAuthenticated });
      
      // If we have a token but no user, fetch the current user
      if (token && !user) {
        console.log('[AuthGuard] Token exists but no user, fetching current user...');
        try {
          const result = await dispatch(fetchCurrentUser()).unwrap();
          console.log('[AuthGuard] Successfully fetched user:', result);
        } catch (error) {
          // If fetching user fails, the token is invalid
          console.error('[AuthGuard] Failed to fetch current user:', error);
        }
      } else {
        console.log('[AuthGuard] Skipping user fetch:', { hasToken: !!token, hasUser: !!user });
      }
      setIsChecking(false);
    };

    checkAuth();
  }, [dispatch, token, user, isAuthenticated]);

  // Show loading state while checking authentication
  if (isChecking || isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requiredRole) {
    // A staff member (merchant_staff / admin_staff) holding the route's
    // staffPermission may pass a role-gated route (backend re-checks the
    // permission + scopes appropriately).
    if (
      (user.role === 'merchant_staff' || user.role === 'admin_staff')
      && staffPermission
      && (staff?.permissions ?? []).includes(staffPermission)
    ) {
      return <>{children}</>;
    }

    const roleHierarchy: Record<string, number> = {
      admin: 3,
      merchant: 2,
      customer: 1,
      // Staff sit below every role-gated route (deny-by-default); they only
      // reach routes with no requiredRole (e.g. /support) or those granting
      // their specific staffPermission. The backend enforces per-permission.
      merchant_staff: 0,
      admin_staff: 0,
    };

    const userRoleLevel = roleHierarchy[user.role];
    const requiredRoleLevel = roleHierarchy[requiredRole];

    // User must have at least the required role level
    if (userRoleLevel < requiredRoleLevel) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default AuthGuard;
